// Builds the view-only vue-flow graph (nodes + edges) from an agent definition.
//
// Layout is deterministic, three columns:
//   left   = enabled configuration options (one node each)
//   center = the agent
//   right  = tools + an empty handover slot (reserved for future sub-agent calls)
//
// Each side node gets its own animated connector to the agent.
import EvaluateItems, { Categories } from '../evaluate/EvaluateItems';
import { aiProviderLogo, dataAdapterLogo, dataAdapterName, emailProviderLogo } from './brand';

// --- geometry -------------------------------------------------------------
const NODE_H = 64; // keep in sync with node min-height in the SFCs
const SIDE_W = 210; // side-node width (keep in sync with .ad-node--side)
const PITCH = NODE_H + 26; // vertical distance between stacked nodes
const COL_LEFT_X = 0;
const COL_CENTER_X = 400;
const COL_RIGHT_X = 800;
const AGENT_W = 230;

// padding of the column container wrappers around their nodes
const GROUP_PAD_X = 18;
const GROUP_PAD_TOP = 42; // leaves room for the container title
const GROUP_PAD_BOTTOM = 18;

// Vertically centre a column of `count` nodes around y = 0 and return tops.
function columnTops(count) {
  const span = (count - 1) * PITCH;
  const firstCenter = -span / 2;
  return Array.from({ length: count }, (_, i) => firstCenter + i * PITCH - NODE_H / 2);
}

// --- labelling ------------------------------------------------------------
const KNOWN_CATEGORY_PREFIXES = ['ai', 'data', 'action', 'document', 'logic', 'crypto', 'response'];

// Nicer names than a raw humanise for the common tools.
const TOOL_LABELS = {
  'data-adapter': 'Data Adapter',
  'data-transform': 'Transform Data',
  'data-chart': 'Chart',
  'data-analysis': 'Analyse Data',
  'action-execute-api': 'Call API',
  'action-send-email': 'Send Email',
  'action-schedule': 'Schedule',
  'action-invoke-flow': 'Invoke Workflow',
  'document-image': 'Image',
  'document-html': 'HTML',
  'document-pdf': 'PDF',
};

function humanise(type) {
  if (!type) return 'Tool';
  const parts = String(type).split('-');
  if (parts.length > 1 && KNOWN_CATEGORY_PREFIXES.includes(parts[0])) parts.shift();
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

function categoryColor(type) {
  const category = (EvaluateItems[type] && EvaluateItems[type].category) || '';
  return (Categories.find((c) => c.key === category) || {}).color || 'text-gray-500';
}

// --- configuration (left column) -----------------------------------------
// Returns the enabled config options as { icon, name, subtitle, logo }.
// `p` is the agent payload (provider/tools/session/inputs/hitl/response live here).
function configOptions(p) {
  const out = [];

  // Session memory — only when a persistence adapter is wired; shows its logo.
  const adapter = p.session && p.session.adapter;
  if (adapter) {
    out.push({ key: 'memory', icon: 'history', name: 'Session Memory', subtitle: dataAdapterName(adapter), logo: dataAdapterLogo(adapter) });
  }

  // Inputs — project variables injected into the agent.
  const inputs = p.inputs || [];
  if (inputs.length) {
    out.push({ key: 'inputs', icon: 'data_object', name: 'Inputs', subtitle: `${inputs.length} variable${inputs.length > 1 ? 's' : ''}` });
  }

  // Human-in-the-loop approvals.
  const hitl = p.hitl || {};
  if (hitl.chat || hitl.email) {
    const channels = [hitl.chat && 'Chat', hitl.email && 'Email'].filter(Boolean).join(' + ');
    out.push({
      key: 'hitl',
      icon: 'gavel',
      name: 'Human in the Loop',
      subtitle: channels,
      logo: hitl.email ? emailProviderLogo(hitl.from && hitl.from.provider) : null,
    });
  }

  // Response mode is always set; surface it as a defining config.
  const mode = (p.response && p.response.mode) || 'stream';
  out.push({ key: 'response', icon: 'chat', name: 'Response', subtitle: mode === 'final' ? 'Final' : 'Stream' });

  return out;
}

// --- tools (right column) -------------------------------------------------
function toolLogo(tool) {
  const payload = tool.payload || {};
  if (tool.type === 'data-adapter') return dataAdapterLogo(payload.adapter_type);
  if (tool.type === 'action-send-email') return emailProviderLogo(payload.from && payload.from.provider);
  return null;
}

function toolSubtitle(tool) {
  const payload = tool.payload || {};
  if (tool.type === 'data-adapter') return dataAdapterName(payload.adapter_type);
  return '';
}

function toolNodeData(tool) {
  const meta = EvaluateItems[tool.type] || {};
  return {
    icon: meta.icon || 'api',
    iconColor: meta.iconColor || categoryColor(tool.type),
    name: TOOL_LABELS[tool.type] || humanise(tool.type),
    subtitle: toolSubtitle(tool),
    logo: toolLogo(tool),
  };
}

// Builds the rounded-rect container wrapper that visually groups a column.
// Uses a plain custom type ('container') with explicit px dimensions rather than
// vue-flow's reserved 'group' type (whose sizing/parenting semantics interfere).
function groupNode(id, title, colX, tops) {
  if (!tops.length) return null;
  const top = tops[0] - GROUP_PAD_TOP;
  const bottom = tops[tops.length - 1] + NODE_H + GROUP_PAD_BOTTOM;
  const width = SIDE_W + GROUP_PAD_X * 2;
  const height = bottom - top;
  return {
    id,
    type: 'container',
    position: { x: colX - GROUP_PAD_X, y: top },
    style: { width: `${width}px`, height: `${height}px` },
    data: { title, width, height },
    draggable: false,
    selectable: false,
    focusable: false,
  };
}

// --- public API -----------------------------------------------------------
export default function buildGraph(agent = {}) {
  const nodes = [];
  const edges = [];

  // Agent config (provider/model/tools/session/inputs/hitl/response) lives under
  // `payload`; the display name is injected onto the agent object by the page.
  const p = agent.payload || agent;

  const configs = configOptions(p);
  const tools = p.tools || [];
  const leftTops = columnTops(configs.length);
  const rightTops = columnTops(tools.length);

  // The agent gets one distinct port per connection so the lines fan out to
  // separate spots instead of all meeting at one centre point. Grow its height
  // so the ports don't overlap, and centre it on y = 0 like the columns.
  const maxPorts = Math.max(configs.length, tools.length, 1);
  const agentHeight = Math.max(NODE_H + 12, maxPorts * 24 + 20);
  const configHandles = configs.map((cfg, i) => ({ id: `h-cfg-${cfg.key}`, top: `${((i + 1) / (configs.length + 1)) * 100}%` }));
  const toolHandles = tools.map((tool, i) => ({ id: `h-tool-${tool.id || i}`, top: `${((i + 1) / (tools.length + 1)) * 100}%` }));

  // container wrappers pushed first so they paint behind the column nodes
  const configGroup = groupNode('group-config', 'Configuration', COL_LEFT_X, leftTops);
  const toolsGroup = groupNode('group-tools', 'Tools', COL_RIGHT_X, rightTops);
  if (configGroup) nodes.push(configGroup);
  if (toolsGroup) nodes.push(toolsGroup);

  // center: the agent
  nodes.push({
    id: 'agent',
    type: 'agent',
    position: { x: COL_CENTER_X, y: -agentHeight / 2 },
    data: {
      name: agent.name || 'Agent',
      model: p.model || '',
      provider: p.provider || '',
      logo: aiProviderLogo(p.provider),
      height: agentHeight,
      configHandles,
      toolHandles,
    },
    draggable: false,
    selectable: false,
  });

  // left: configuration options — each wired to its own agent port
  configs.forEach((cfg, i) => {
    const id = `cfg-${cfg.key}`;
    nodes.push({
      id,
      type: 'config',
      position: { x: COL_LEFT_X, y: leftTops[i] },
      data: cfg,
      draggable: false,
      selectable: false,
    });
    edges.push({
      id: `e-${id}`,
      source: id,
      sourceHandle: 'out',
      target: 'agent',
      targetHandle: configHandles[i].id,
      type: 'default',
      animated: true,
      class: 'agent-edge',
    });
  });

  // right: tools (incl. any future agent-handover tool) — each on its own port
  tools.forEach((tool, i) => {
    const id = `tool-${tool.id || i}`;
    nodes.push({
      id,
      type: 'tool',
      position: { x: COL_RIGHT_X, y: rightTops[i] },
      data: toolNodeData(tool),
      draggable: false,
      selectable: false,
    });
    edges.push({
      id: `e-${id}`,
      source: 'agent',
      sourceHandle: toolHandles[i].id,
      target: id,
      targetHandle: 'in',
      type: 'default',
      animated: true,
      class: 'agent-edge',
    });
  });

  return { nodes, edges, agentWidth: AGENT_W };
}
